import { Box, Grid2, TextField } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useNavigationGuard from "../../../hooks/useNavigationGuard";
import {
  checkCanUpdate,
  notifyError,
  notifySuccess,
} from "../../../utilities/helper";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  addLotTypeManagementService,
  fetchLotByIdService,
  updateLotTypeManagementService,
} from "../../../services/lotManagementServices";
import { MenuContext } from "../../../App";
import { CreateHandleUnsaveNavigation } from "../../../utilities/handleUnsaveNavigation";

const AddUpdateLotManagement = () => {
  const id = useLocation()?.state?.id;

  const [load, setLoad] = useState(false);

  const [canUpdateLot, setCanUpdateLot] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const navigate = useNavigate();

  const { systemActions } = useContext(MenuContext);
  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();

  const access_token = localStorage.getItem("access_token");
  const role = localStorage.getItem("role");

  const createLotSchema = yup.object().shape({
    unblindedLotNumber: yup
      .string()
      .required("Unblinded Lot Number is required"),
    notes: yup.string().required("Notes is required"),
    blindedLotNumber: yup.string().required("Blinded Lot Number is required"),
    expirationDate: yup
      .mixed()
      .test("is-valid-date", "Expiration date is required", (value) =>
        dayjs(value).isValid(),
      )
      .nullable(),
    stopShipmentDays: yup
      .string()
      .matches(/^\d+(\.\d+)?$/, "Stop Shipment Days is not valid.")
      .required("Stop Shipment Days is required"),
    doNotCountDays: yup
      .string()
      .matches(/^\d+(\.\d+)?$/, "Do Not Count Days is not valid.")
      .required("Do Not Count Days  is required"),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createLotSchema),
    defaultValues: {
      unblindedLotNumber: "",
      blindedLotNumber: "",
      expirationDate: "",
      stopShipmentDays: "",
      doNotCountDays: "",
      notes: "",
    },
  });

  useNavigationGuard(isDirty);

  useEffect(() => {
    if (id) {
      getLotDetailsById(id);
    }
  }, [id]);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin" && id) {
      let canUpdate = false;
      const updateFound = systemActions?.find(
        (item) => item?.name === "CanEditLotList",
      );
      if (updateFound) {
        canUpdate = true;
      }
      setCanUpdateLot(canUpdate);
    } else {
      if (!id || role === "Admin") {
        setCanUpdateLot(true);
      }
    }
  }, [systemActions, id]);

  useEffect(() => {
    if (canUpdateLot) {
      setIsReadOnly(false);
    } else {
      setIsReadOnly(true);
    }
  }, [canUpdateLot]);

  const getLotDetailsById = async (id) => {
    setLoad(true);
    try {
      const res = await fetchLotByIdService(access_token, id);
      if (res?.status == 200) {
        const resp = res?.data?.result;
        setLoad(false);
        reset({
          blindedLotNumber: resp?.blindedNumber,
          doNotCountDays: resp.doNotCountDays,
          expirationDate: resp.expirationDate,
          notes: resp?.notes,
          stopShipmentDays: resp?.stopShipmentDays,
          unblindedLotNumber: resp?.unblindedNumber,
        });
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err?.message);
      if (err?.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const addUpdateLotDetails = async (data) => {
    const newData = {
      unblindedNumber: data?.unblindedLotNumber,
      blindedNumber: data?.blindedLotNumber,
      expirationDate: data?.expirationDate,
      stopShipmentDays: data?.stopShipmentDays,
      doNotCountDays: data?.doNotCountDays,
      notes: data?.notes,
    };
    setLoad(true);
    try {
      if (!id) {
        const res = await addLotTypeManagementService(access_token, newData);
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Lot Management is created successfully !");
          reset();
          navigate("/lots-management");
        }
      } else {
        const res = await updateLotTypeManagementService(
          access_token,
          newData,
          id,
        );
        if (res?.status == 200) {
          setLoad(false);
          notifySuccess("Lot Management is Updated successfully !");
          reset();
          navigate("/lots-management");
        }
      }
    } catch (err) {
      setLoad(false);
      console.log(err.message);
      notifyError(err.message);
      if (err.status == 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const stopShipmentDaysRegister = register("stopShipmentDays");
  const doNotCountDaysRegister = register("doNotCountDays");

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div className="content-body">
          <p className="bread-crumbs-link-container">
            <button
              className="bread-crumbs-btn"
              onClick={(e) => handleUnsaveNavigation(e, isDirty, "", navigate)}
            >
              Home
            </button>{" "}
            |{" "}
            <button
              className="bread-crumbs-btn"
              onClick={(e) =>
                handleUnsaveNavigation(e, isDirty, "lots-management", navigate)
              }
            >
              Lot Management
            </button>{" "}
            |{" "}
            <button className="bread-crumbs-btn" to="/addUpdate-lot-management">
              {id ? "Edit Lot Management" : "Add Lot Management"}
            </button>
            -{" "}
            <b style={{ color: "gray" }}>
              {localStorage?.getItem("studyName")}
            </b>
          </p>
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", margin: "0 auto" }}
          >
            <Grid2 item md={12}>
              <p className="page-heading">Lot Details</p>
            </Grid2>
          </Grid2>

          <Box
            component="form"
            autoComplete="off"
            sx={{ height: "auto", width: "100%" }}
            onSubmit={handleSubmit(addUpdateLotDetails)}
          >
            <Grid2
              container
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ width: "100%", margin: "0 auto" }}
              gap={1}
            >
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Unblinded Lot Number
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    fullWidth
                    disabled={isReadOnly}
                    type={"text"}
                    {...register("unblindedLotNumber")}
                  />
                </div>
                {errors.unblindedLotNumber && (
                  <div className="error-container">
                    {errors.unblindedLotNumber.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Blinded Lot Number
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    disabled={isReadOnly}
                    size="small"
                    type={"text"}
                    {...register("blindedLotNumber")}
                  />
                </div>
                {errors.blindedLotNumber && (
                  <div className="error-container">
                    {errors.blindedLotNumber.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Notes
                    <span>*</span>
                  </p>
                  <TextField
                    size="small"
                    fullWidth
                    disabled={isReadOnly}
                    type={"text"}
                    {...register("notes")}
                  />
                </div>
                {errors.notes && (
                  <div className="error-container">{errors.notes.message}</div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Expiration Date<span>*</span>
                  </p>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Controller
                      name="expirationDate"
                      control={control}
                      rules={{ required: "Expiration date is required" }} // Optional validation rule
                      render={({ field }) => (
                        <DateTimePicker
                          sx={{ width: "100%" }}
                          disabled={isReadOnly}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) => field.onChange(date)}
                          minDateTime={dayjs()} // Prevents selecting past date and time
                          format="DD-MMM-YYYY hh:mm A"
                          slotProps={{
                            textField: {
                              placeholder: "DD-MMM-YYYY hh:mm A",
                              sx: {
                                "& .MuiInputBase-root": { height: "40px" },
                              },
                              size: "small",
                              fullWidth: true,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </div>
                {errors.expirationDate && (
                  <div className="error-container">
                    {errors.expirationDate.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Stop Shipment Days
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    disabled={isReadOnly}
                    size="small"
                    type={"number"}
                    {...stopShipmentDaysRegister}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
                      if (value !== "0") value = value.replace(/^0+/, ""); // Remove leading zeros
                      e.target.value = value;
                      stopShipmentDaysRegister.onChange(e);
                    }}
                  />
                </div>
                {errors.stopShipmentDays && (
                  <div className="error-container">
                    {errors.stopShipmentDays.message}
                  </div>
                )}
              </Grid2>
              <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
                <div>
                  <p className="labelStyles">
                    Do Not Count In Days
                    <span>*</span>
                  </p>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={isReadOnly}
                    type={"number"}
                    {...doNotCountDaysRegister}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
                      if (value !== "0") value = value.replace(/^0+/, ""); // Remove leading zeros
                      e.target.value = value;
                      doNotCountDaysRegister.onChange(e);
                    }}
                  />
                </div>
                {errors.doNotCountDays && (
                  <div className="error-container">
                    {errors.doNotCountDays.message}
                  </div>
                )}
              </Grid2>
            </Grid2>

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "lots-management",
                      navigate,
                    );
                  }}
                >
                  Cancel
                </button>
              </Grid2>
              <Grid2 item md={6}>
                {checkCanUpdate(id, isReadOnly, role) && (
                  <button
                    type="submit"
                    disabled={isReadOnly}
                    className={isReadOnly ? "disabled-btn" : "create-btn"}
                  >
                    {id ? "Update" : "Save"}
                  </button>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </div>
      )}
    </>
  );
};

export default AddUpdateLotManagement;
